import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FolderOpen,
  Users,
  Calendar,
  Target,
  BarChart3,
  FileText,
  Settings,
  ArrowRight,
  Clock,
  CheckCircle,
  AlertTriangle,
  TrendingUp
} from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const ProjectsLanding = () => {
  const navigate = useNavigate();

  const projectCards = [
    {
      id: 'project-dashboard',
      title: 'Project Dashboard',
      description: 'Overview of project activities & performance',
      icon: <BarChart3 size={24} />,
      path: '/projects/dashboard',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['KPI metrics', 'Performance charts', 'Risk analysis']
    },
    {
      id: 'project-management',
      title: 'Project Management',
      description: 'Manage projects with milestones & timelines',
      icon: <FolderOpen size={24} />,
      path: '/projects/management',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Project creation', 'Milestone tracking', 'Timeline management']
    },
    {
      id: 'milestone-tracking',
      title: 'Milestone Tracking',
      description: 'Track project milestones & deliverables',
      icon: <Target size={24} />,
      path: '/milestones',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Milestone planning', 'Progress tracking', 'Deliverable management']
    },
    {
      id: 'task-management',
      title: 'Task Management',
      description: 'Assign, track & manage project tasks',
      icon: <Target size={24} />,
      path: '/projects/tasks',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Task assignment', 'Progress tracking', 'Status updates']
    },
    {
      id: 'team-collaboration',
      title: 'Team Collaboration',
      description: 'Manage team members & project collaboration',
      icon: <Users size={24} />,
      path: '/team/management',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Team management', 'Role assignment', 'Collaboration tools']
    },
    {
      id: 'risk-management',
      title: 'Risk Management',
      description: 'Identify, assess & manage project risks',
      icon: <AlertTriangle size={24} />,
      path: '/projects/risks',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Risk assessment', 'Issue tracking', 'Mitigation planning']
    },
    {
      id: 'time-tracking',
      title: 'Time Tracking',
      description: 'Track time spent on projects & tasks',
      icon: <Clock size={24} />,
      path: '/projects/time-tracking',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Time logging', 'Timesheet management', 'Billing integration']
    },
    {
      id: 'project-reports',
      title: 'Project Reports',
      description: 'Generate detailed project reports & analytics',
      icon: <FileText size={24} />,
      path: '/projects/reports',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Progress reports', 'Resource utilization', 'Performance metrics']
    }
    // {
    //   id: 'project-settings',
    //   title: 'Project Settings',
    //   description: 'Configure project templates & workflows',
    //   icon: <Settings size={24} />,
    //   path: '/projects/settings',
    //   iconBg: '#D6EFFF',
    //   iconLine: '#2D7DD2',
    //   features: ['Templates', 'Workflows', 'Configuration']
    // }
  ];

  const handleCardClick = (path) => {
    navigate(path);
  };

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader
        title="Project Management"
        description="Comprehensive project lifecycle management and team collaboration"
      />
      <div className="page-container">
        {/* Project Management Cards */}
        <div
          className="grid-modern-3"
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            width: '100%'
          }}
        >
          {projectCards.map((card) => (
            <div
              key={card.id}
              className="asset-landing-card"
              onClick={() => handleCardClick(card.path)}
            >
              {/* Top Accent Line */}
              <div
                style={{
                  height: '4px',
                  background: `var(--gold-primary)`,
                  width: '100%'
                }}
              />

              {/* Card Content */}
              <div style={{ padding: '24px' }}>
                {/* Icon and Title */}
                <div className="d-flex align-items-center mb-3">
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      background: '#FFF9E6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '16px'
                    }}
                  >
                    <div style={{ color: 'var(--gold-primary)' }} className="asset-card-icon">
                      {card.icon}
                    </div>
                  </div>
                  <div className="flex-grow-1">
                    <h4 style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: 'var(--text-primary)',
                      margin: '0',
                      fontFamily: 'var(--font-family)'
                    }}>
                      {card.title}
                    </h4>
                  </div>
                  <ArrowRight
                    size={20}
                    className="asset-card-arrow"
                  />
                </div>

                {/* Description */}
                <p style={{
                  fontSize: '13px',
                  color: 'var(--text-secondary)',
                  margin: '0 0 12px 0',
                  lineHeight: '1.4',
                  fontFamily: 'var(--font-family)'
                }}>
                  {card.description}
                </p>

                {/* Features */}
                <div>
                  <div style={{
                    fontSize: '11px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Key Features
                  </div>
                  <div className="d-flex flex-wrap gap-1">
                    {card.features.map((feature, index) => (
                      <span
                        key={index}
                        style={{
                          fontSize: '12px',
                          padding: '4px 8px',
                          background: '#f3f4f6',
                          color: '#374151',
                          borderRadius: '6px',
                          fontWeight: '500',
                        }}
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default ProjectsLanding;
